import { useEffect, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { useNavigate } from "react-router-dom";

import { PAGES_DOMAIN_URL } from "constants";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import AutoresponderData from "./AutoresponderData";

export default ({ autoresponderId }) => {
  const navigate = useNavigate();

  const [loadingStatusToggle, setLoadingStatusToggle] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [name, setName] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [editError, setEditError] = useState(null);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Fetch data
  const AUTORESPONDER_DATA = gql`
    query GetAutoresponderData($responderId: ID!) {
      responderData(responderId: $responderId) {
        id
        name
        lists {
          id
          name
        }
        status
        over_limit
        leads {
          id
          created_at
        }
        leads_aggregate {
          aggregate {
            count
          }
        }
        sent_emails {
          id
          created_at
        }
        sent_emails_aggregate {
          aggregate {
            count
          }
        }
      }
    }
  `;

  const { data, error, refetch } = useQuery(AUTORESPONDER_DATA, {
    variables: {
      responderId: autoresponderId,
    },
    skip: !autoresponderId,
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("error fetching autoresponder data", error);
    captureException(error);
  }

  useEffect(() => {
    if (data) {
      if (!data.responderData) {
        navigate("/autoresponders");
        return;
      }

      setName(data.responderData.name);
    }
  }, [data]);

  // handle Status toggle
  const CHANGE_RESPONDER_STATUS = gql`
    mutation ChangeResponderStatus($responderId: ID!) {
      changeResponderStatus(responderId: $responderId) {
        id
        status
      }
    }
  `;

  const [changeResponderStatus] = useMutation(CHANGE_RESPONDER_STATUS);

  const onStatusToggleClick = async () => {
    try {
      if (!data) return;

      setLoadingStatusToggle(true);

      await changeResponderStatus({
        variables: {
          responderId: autoresponderId,
        },
      });

      setLoadingStatusToggle(false);
    } catch (error) {
      console.log("error onStatusToggleClick", error);
      captureException(error);
      setLoadingStatusToggle(false);
      return;
    }
  };

  // handle Edit name
  const onEditClick = () => {
    setEditModal(true);
  };

  const closeEditModal = () => {
    setEditModal(false);
  };

  const handleInputChange = (e) => {
    setName(e.target.value);
  };

  const UPDATE_RESPONDER = gql`
    mutation UpdateResponder($responderId: ID!, $name: String!) {
      updateResponder(responderId: $responderId, name: $name) {
        resultCode
      }
    }
  `;

  const [updateResponder] = useMutation(UPDATE_RESPONDER);

  const onEditAutoresponder = async () => {
    try {
      if (!data) return;

      if (name.length < 1) {
        setWarnings(["name"]);
        return;
      }

      setWarnings([]);
      setEditError(null);

      setLoadingEdit(true);

      const result = await updateResponder({
        variables: {
          responderId: autoresponderId,
          name,
        },
      });

      if (result.data.updateResponder.resultCode !== 0) {
        setLoadingEdit(false);
        setEditError(
          "An error occurred while submitting the request. Please try again or contact support."
        );
        return;
      }

      setEditModal(false);
      refetch();

      setLoadingEdit(false);
    } catch (error) {
      console.log("error onEditAutoresponder", error);
      captureException(error);
      setLoadingEdit(false);
      return;
    }
  };

  // handle duplicate Autoresponder
  const onDuplicateClick = () => {
    setConfirmDuplicate(true);
  };

  const closeDuplicateModal = () => {
    setConfirmDuplicate(false);
    setDuplicateError(null);
  };

  const DUPLICATE_RESPONDER = gql`
    mutation DuplicateResponder($responderId: ID!) {
      duplicateResponder(responderId: $responderId) {
        resultCode
      }
    }
  `;

  const [duplicateResponder] = useMutation(DUPLICATE_RESPONDER);

  const onConfirmDuplicate = async () => {
    try {
      if (!confirmDuplicate) return;

      if (loadingDuplicate) return;

      setDuplicateError(null);
      setLoadingDuplicate(true);

      const result = await duplicateResponder({
        variables: {
          responderId: autoresponderId,
        },
      });

      const { resultCode } = result.data.duplicateResponder;

      console.log({ resultCode });

      if (resultCode === 8) {
        setDuplicateError(
          "You can have a maximum of 10 autoresponders. Please delete an older responder before creating a new one."
        );
        setLoadingDuplicate(false);
        return;
      }

      if (resultCode !== 0) {
        setLoadingDuplicate(false);
        setDuplicateError(
          "An error occurred while submitting the request. Please try again or contact support."
        );
        return;
      }

      setLoadingDuplicate(false);
      setConfirmDuplicate(null);
      navigate("/autoresponders");
    } catch (error) {
      console.log("onConfirmDuplicate error", error);
      captureException(error);
      setLoadingDuplicate(false);
      setDuplicateError(
        "An error occurred while submitting the request. Please try again or contact support."
      );
      return;
    }
  };

  // handle delete Autoresponder
  const onDeleteClick = () => {
    setConfirmDelete(true);
  };

  const closeDeleteModal = () => {
    setConfirmDelete(false);
    setDeleteError(null);
  };

  const DELETE_RESPONDER = gql`
    mutation DeleteResponder($responderId: ID!) {
      deleteResponder(responderId: $responderId) {
        resultCode
      }
    }
  `;

  const [deleteResponder] = useMutation(DELETE_RESPONDER);

  const onConfirmDelete = async () => {
    try {
      if (!confirmDelete) return;

      if (loadingDelete) return;

      setDeleteError(null);
      setLoadingDelete(true);

      const result = await deleteResponder({
        variables: {
          responderId: autoresponderId,
        },
      });

      const { resultCode } = result.data.deleteResponder;

      console.log({ resultCode });

      if (resultCode !== 0) {
        setLoadingDelete(false);
        setDeleteError(
          "An error occurred while submitting the request. Please try again or contact support."
        );
        return;
      }

      setLoadingDelete(false);
      setConfirmDelete(null);
      navigate("/autoresponders");
    } catch (error) {
      console.log("onConfirmDelete error", error);
      captureException(error);
      setLoadingDelete(false);
      setDeleteError(
        "An error occurred while submitting the request. Please try again or contact support."
      );
      return;
    }
  };

  //console.log("data", data);

  return (
    <>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="280px" />
        </LoadingBox>
      )}
      {data?.responderData && (
        <AutoresponderData
          data={data}
          onStatusToggleClick={onStatusToggleClick}
          loadingStatusToggle={loadingStatusToggle}
          editModal={editModal}
          onEditClick={onEditClick}
          name={name}
          handleInputChange={handleInputChange}
          closeEditModal={closeEditModal}
          warnings={warnings}
          loadingEdit={loadingEdit}
          editError={editError}
          onEditAutoresponder={onEditAutoresponder}
          onDuplicateClick={onDuplicateClick}
          closeDuplicateModal={closeDuplicateModal}
          onConfirmDuplicate={onConfirmDuplicate}
          confirmDuplicate={confirmDuplicate}
          duplicateError={duplicateError}
          loadingDuplicate={loadingDuplicate}
          onDeleteClick={onDeleteClick}
          onConfirmDelete={onConfirmDelete}
          confirmDelete={confirmDelete}
          deleteError={deleteError}
          loadingDelete={loadingDelete}
          closeDeleteModal={closeDeleteModal}
        />
      )}
    </>
  );
};
